import { useId, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ProductLengthPicker,
  type PickerLeadingOption,
} from '~/components/common/ProductLengthPicker';
import { useLengthPickerLabels } from '~/components/common/useLengthPickerLabels';
import { RandomTallyBundlesGrid } from '~/components/common/RandomTallyBundlesGrid';
import {
  bundleCountTexts,
  sortedByLength,
  type RandomBundlesByProductId,
} from '~/components/common/tallyBundleCounts';
import { BundleQuantityInput } from '~/components/ui/BundleQuantityInput';
import { TallyCountStepper } from '~/components/ui/TallyCountStepper';
import { CatalogProductSearch } from '~/components/common/CatalogProductSearch';
import { useCatalogLengths } from '~/api/productLengths';
import type { CannedTallyOption } from '~/components/common/productLengthChoice';
import type { ProductLengthFieldsState } from '~/components/common/useProductLengthPick';

const RANDOM_TALLY_OPTION_ID = 'random-tally';
const NO_EXISTING_COUNT = () => undefined;
const NO_EXISTING_BUNDLES: RandomBundlesByProductId = {};

type ProductLengthQuantityFieldsProps = Readonly<{
  testId: string;
  fields: ProductLengthFieldsState;
  existingCannedTallyCount?: (
    cannedTally: CannedTallyOption
  ) => number | undefined;
  existingRandomBundles?: RandomBundlesByProductId;
  disabled?: boolean;
  children?: ReactNode;
}>;

function cannedTallyBreakdown(bundles: CannedTallyOption['bundles']): string {
  return sortedByLength(bundles)
    .map(
      (bundle) =>
        `${bundle.platformProductsLength.lengthDisplayValue} ×${bundle.bundleCount}`
    )
    .join(' · ');
}

/**
 * One row of catalog product search, length and quantity, for adding any product
 * whether or not the buyer stocks it. The state and the resulting pick come from
 * `useProductLengthPick`. When the product has a bundle size, the Length dropdown
 * also offers a random tally and the product's canned tallies.
 *
 * `existingCannedTallyCount` (the saved count of a canned tally, if any) and
 * `existingRandomBundles` (by product id, then length id) preload a tally that is
 * already saved. Extra fields and the submit button go in `children`, on the same row.
 * When several lengths are ticked, each gets its own quantity in a table below the row.
 *
 * @example
 *   const { pick, fields, resetKey } = useProductLengthPick({ transportVehicleId });
 *   <ProductLengthQuantityFields
 *     key={resetKey}
 *     testId="add-row"
 *     fields={fields}
 *     existingCannedTallyCount={(cannedTally) => savedCounts[cannedTally.id]}
 *   >
 *     <Button disabled={!pick.isComplete} onClick={save}>Add</Button>
 *   </ProductLengthQuantityFields>
 */
export function ProductLengthQuantityFields({
  testId,
  fields,
  existingCannedTallyCount = NO_EXISTING_COUNT,
  existingRandomBundles = NO_EXISTING_BUNDLES,
  disabled = false,
  children,
}: ProductLengthQuantityFieldsProps) {
  const { t } = useTranslation('general');
  const quantityHeadingId = useId();
  const tallyCountInputId = useId();
  const {
    product,
    choice,
    length,
    productLength,
    quantity,
    hasBundleSize,
    canPickRandomTally,
    lengthCount,
    onlyLength,
    lengthsError,
    cannedTallies,
  } = fields;
  const productId = product ? product.id : '';

  const tallyOptions = (): PickerLeadingOption[] => {
    if (!hasBundleSize) return [];
    const randomLabel = t('product_length_picker.random_tally');
    const cannedBadge = t('product_length_picker.canned_tally_badge');
    const randomTallyOption: PickerLeadingOption = {
      id: RANDOM_TALLY_OPTION_ID,
      searchText: randomLabel,
      content: randomLabel,
      onSelect: () =>
        fields.pickRandomTally(
          bundleCountTexts(existingRandomBundles[productId])
        ),
    };
    return [
      ...(canPickRandomTally ? [randomTallyOption] : []),
      ...cannedTallies.map((cannedTally) => ({
        id: `canned-tally-${cannedTally.id}`,
        searchText: `${cannedTally.name} ${cannedBadge}`,
        content: (
          <span className="flex flex-col gap-0.5">
            <span className="flex items-center gap-2">
              <span>{cannedTally.name}</span>
              <span className="rounded bg-status-info-50 px-1.5 text-xs font-semibold text-status-info-800">
                {cannedBadge}
              </span>
            </span>
            <span className="text-xs text-neut-sec">
              {cannedTallyBreakdown(cannedTally.bundles)}
            </span>
          </span>
        ),
        onSelect: () =>
          fields.pickCannedTally(
            cannedTally,
            existingCannedTallyCount(cannedTally) ?? 1
          ),
      })),
    ];
  };

  const triggerLabel = () => {
    if (choice.kind === 'canned') return choice.cannedTally.name;
    if (choice.kind === 'lengths') {
      return choice.lines
        .map((line) => line.length.length.displayValue)
        .join(', ');
    }
    if (choice.kind === 'random')
      return t('product_length_picker.random_tally');
    return (
      (length && length.length.displayValue) ??
      t('product_length_picker.length_trigger')
    );
  };

  const pickerLabels = useLengthPickerLabels(
    t('product_length_picker.length'),
    triggerLabel()
  );

  const renderLength = () => {
    if (!hasBundleSize && (lengthCount === 0 || onlyLength)) {
      return (
        <div className="flex w-36 shrink-0 flex-col gap-1">
          <span className="text-xs font-medium text-neut-sec">
            {t('product_length_picker.length')}
          </span>
          {onlyLength ? (
            <p
              className="py-2 text-sm text-neut-prim"
              data-testid={`${testId}-length-only`}
            >
              {onlyLength.length.displayValue}
            </p>
          ) : (
            <p
              className="py-2 text-sm text-neut-sec"
              data-testid={`${testId}-length-none`}
            >
              {t('product_length_picker.no_lengths')}
            </p>
          )}
        </div>
      );
    }
    return (
      <ProductLengthPicker
        className="w-36 shrink-0"
        platformProductId={productId}
        disabled={
          disabled || !product || (lengthCount === undefined && !lengthsError)
        }
        testId={`${testId}-length`}
        labels={pickerLabels}
        fetchHook={useCatalogLengths}
        onSelect={fields.pickLength}
        leadingOptions={tallyOptions()}
        selectedIds={fields.selectedLengthIds}
      />
    );
  };

  const renderQuantity = () => {
    if (choice.kind === 'canned') {
      return (
        <TallyCountStepper
          inputId={tallyCountInputId}
          value={choice.tallyCount}
          onChange={fields.changeTallyCount}
          disabled={disabled}
        />
      );
    }
    if (choice.kind === 'random') {
      return (
        <p
          className="py-2 text-sm text-neut-sec"
          data-testid={`${testId}-random-hint`}
        >
          {t('product_length_picker.random_tally_hint')}
        </p>
      );
    }
    if (choice.kind === 'lengths') {
      return (
        <p
          className="py-2 text-sm text-neut-sec"
          data-testid={`${testId}-lengths-hint`}
        >
          {t('product_length_picker.several_lengths_hint')}
        </p>
      );
    }
    if (productLength && quantity) {
      return (
        <BundleQuantityInput
          key={productLength.id}
          quantity={quantity}
          platformProductsLength={productLength}
          piecesPerBundle={
            choice.kind === 'length' ? choice.piecesPerBundle : undefined
          }
          onBundleChange={fields.changeQuantity}
          disabled={disabled}
        />
      );
    }
    return (
      <p className="py-2 text-sm text-neut-sec">
        {t('product_length_picker.quantity_hint')}
      </p>
    );
  };

  return (
    <div data-testid={testId} className="flex flex-wrap items-start gap-3">
      <CatalogProductSearch
        testId={testId}
        product={product}
        onProductChange={fields.changeProduct}
        disabled={disabled}
      />

      {renderLength()}

      <div
        role="group"
        aria-labelledby={quantityHeadingId}
        className="flex w-56 shrink-0 flex-col gap-1"
      >
        {choice.kind === 'canned' ? (
          <label
            id={quantityHeadingId}
            htmlFor={tallyCountInputId}
            className="text-xs font-medium text-neut-sec"
          >
            {t('product_length_picker.tally_count')}
          </label>
        ) : (
          <span
            id={quantityHeadingId}
            className="text-xs font-medium text-neut-sec"
          >
            {t('product_length_picker.quantity')}
          </span>
        )}
        {renderQuantity()}
      </div>

      {choice.kind === 'lengths' && (
        <div
          className="order-last basis-full"
          data-testid={`${testId}-lengths`}
        >
          <table className="text-sm">
            <thead>
              <tr className="text-left text-neut-sec">
                <th className="w-24 pb-2 font-normal">
                  {t('product_length_picker.length')}
                </th>
                <th className="w-56 pb-2 font-normal">
                  {t('product_length_picker.quantity')}
                </th>
              </tr>
            </thead>
            <tbody>
              {fields.lengthLines.map((line) => (
                <tr
                  key={line.length.id}
                  className="border-t border-neut-border"
                  data-testid={`${testId}-lengths-row-${line.length.id}`}
                >
                  <th
                    scope="row"
                    className="py-2 pr-4 text-left font-normal text-neut-prim"
                  >
                    {line.length.length.displayValue}
                  </th>
                  <td className="py-2">
                    <BundleQuantityInput
                      quantity={line.quantity}
                      platformProductsLength={line.length.platformProductLength}
                      piecesPerBundle={line.piecesPerBundle}
                      onBundleChange={(nextQuantity) =>
                        fields.changeLineQuantity(line.length.id, nextQuantity)
                      }
                      disabled={disabled}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {product && choice.kind === 'random' && (
        <div
          className="order-last max-w-sm basis-full"
          data-testid={`${testId}-random-tally`}
        >
          <RandomTallyBundlesGrid
            platformProductId={product.id}
            bundleCountsByLengthId={choice.bundleCountsByLengthId}
            onBundleCountChange={fields.changeBundleCount}
            piecesPerBundle={product.piecesPerBundle}
            testId={`${testId}-random-grid`}
            disabled={disabled}
          />
        </div>
      )}

      {children}
    </div>
  );
}
